import React, { PureComponent } from 'react';
import classNames from 'classnames';
import _ from 'lodash';

import { Divider } from '@eventbrite/eds-divider';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { Icon } from '@eventbrite/eds-icon';
import { getIsUserInSubscriptionSupportedTld } from '@eventbrite/pricing-and-packaging-ui';

import {
    ANNUAL_FEE_TITLE,
    DEFAULT_PACKAGE_TYPES,
    getCappedFeeTitleOverThreshold,
    getCappedFeeTitleUnderThreshold,
    ITEM_TYPES_SHOWN_IN_PRICING_PAGE,
    PLAN_DETAILS,
    ROW_TITLE_PER_ITEM_TYPE,
    LOCALES_WITH_MULTIPLE_ITEM_TYPES_PRICES,
} from '../../constants/main';
import {
    ANNUAL_FEE_CUSTOM,
    ANNUAL_FEE_NONE,
    ANNUAL_PRICE_PER_LOCALE,
} from '../../constants/signupFeatures';

import './featureComparisonTable.scss';

import { CheckChunky, MinusChunky } from '@eventbrite/eds-iconography';
import {
    getFeesForTld,
    getOrganizerFeePerPlan,
} from '../pricingAndPackagingUtils';

const isTldAllowedForNewPricingPage = getIsUserInSubscriptionSupportedTld();
/*
Icon type constants
*/
const AVAILABLE_ICON_COLOR = 'ui-orange';
const UNAVAILABLE_ICON_COLOR = 'ui-900';
const AVAILABLE_ICON_TYPE = <CheckChunky />;
const UNAVAILABLE_ICON_TYPE = <MinusChunky />;

/*
Classname constants
*/
const PRICE_PER_TICKET_PRICING_CELLS_CLASS_NAME =
    'eds-text--center eds-g-cell eds-g-cell-6-12 eds-g-cell-mn-2-12 eds-g-cell-md-2-12 eds-g-cell-mw-2-12 eds-g-cell-ln-2-12 eds-g-cell-lg-2-12 eds-g-cell-lw-2-12';
const FEATURE_AVAILABILITY_ICON_CLASS_NAME_DEFAULT = classNames(
    'eds-text--center',
    { 'cpp-divider eds-l-pad-vert-3': isTldAllowedForNewPricingPage },
    'eds-g-cell eds-g-cell-6-12',
    { 'eds-g-cell-mn-2-12': !isTldAllowedForNewPricingPage },
    { 'eds-g-cell-mn-6-12': isTldAllowedForNewPricingPage },
    'eds-g-cell-md-2-12 eds-g-cell-mw-2-12 eds-g-cell-ln-2-12 eds-g-cell-lg-2-12 eds-g-cell-lw-2-12',
);
const FEATURE_AVAILABILITY_ICON_CLASS_NAME_TWO_PACKAGES =
    'eds-text--center eds-g-cell eds-g-cell-6-12 eds-g-cell-mn-3-12 eds-g-cell-md-3-12 eds-g-cell-mw-3-12 eds-g-cell-ln-3-12 eds-g-cell-lg-3-12 eds-g-cell-lw-3-12';
const FEATURE_AVAILABILITY_STRING_CLASS_NAME_DEFAULT = classNames(
    'eds-text--center',
    { 'cpp-divider eds-l-pad-vert-3': isTldAllowedForNewPricingPage },
    'eds-text-bm eds-g-cell eds-g-cell-6-12',
    { 'eds-g-cell-mn-2-12': !isTldAllowedForNewPricingPage },
    { 'eds-g-cell-mn-6-12': isTldAllowedForNewPricingPage },
    'eds-g-cell-md-2-12 eds-g-cell-mw-2-12 eds-g-cell-ln-2-12 eds-g-cell-lg-2-12 eds-g-cell-lw-2-12',
);
const FEATURE_AVAILABILITY_STRING_CLASS_NAME_TWO_PACKAGES =
    'eds-text--center eds-text-bm eds-g-cell eds-g-cell-6-12 eds-g-cell-mn-3-12 eds-g-cell-md-3-12 eds-g-cell-mw-3-12 eds-g-cell-ln-3-12 eds-g-cell-lg-3-12 eds-g-cell-lw-3-12';
const FEATURE_LINE_CONTAINER_CLASS_NAME =
    'eds-g-group eds-l-pad-vert-3 eds-l-pad-top-3 eds-align--center-vertical';
const FEATURE_LINE_CONTAINER_NOPAD_CLASS_NAME =
    'eds-g-group eds-align--center-vertical';
const FEATURE_LINE_DIVIDER_MEDIUM_CLASS_NAME =
    'eds-g-offset-0-12 eds-g-offset-mw-1-12 eds-g-cell-12-12 eds-g-cell-mw-10-12 eds-g-cell-ln-10-12 eds-g-cell-lg-10-12 eds-g-cell-lw-10-12';
const FEATURE_LINE_DIVIDER_LONG_CLASS_NAME =
    'eds-g-offset-0-12 eds-g-offset-mw-1-12 eds-g-cell-12-12 eds-g-cell-mw-11-12 eds-g-cell-ln-11-12 eds-g-cell-lg-11-12 eds-g-cell-lw-11-12';
const FEATURE_BLOCK_ICON_CLASS_NAME =
    'eds-g-offset-0-12 eds-g-offset-mw-1-12 eds-g-cell eds-g-cell-1-10 eds-l-pad-right-2';

/*
Price per paid ticket (e.g. 1% + $0.99 Max $19.95 / ticket) cells
*/
const ItemPriceCell = ({
    formattedFee,
    formattedMax,
    taxInformation: { taxDescription, needTaxDescription },
    isSimplePricing,
    showMaxFee,
    planIndex,
}) => {
    const isTabMode = !_.isNil(planIndex);
    const pricingClassName = classNames({
        'eds-text-bl': !isSimplePricing && !needTaxDescription,
        'eds-text-bm': needTaxDescription && !isTabMode,
        'eds-text-bs': isTabMode,
    });
    let perTicketLabel = null;
    let planFeeInfo = (
        <p className={pricingClassName}>
            {`${formattedFee} ${taxDescription}`}
        </p>
    );

    if (showMaxFee && formattedMax && !isSimplePricing) {
        perTicketLabel = (
            <p className="eds-text-bs eds-text--light">{formattedMax}</p>
        );
    }

    if (needTaxDescription) {
        planFeeInfo = (
            <div className="eds-g-cell eds-g-cell-1-1">
                <p className={pricingClassName}>{formattedFee}</p>
                <p className="tax-description-muted">{taxDescription}</p>
            </div>
        );
    }

    if (isTabMode && needTaxDescription) {
        planFeeInfo = (
            <div className="eds-g-cell eds-g-cell-1-1">
                <p className={pricingClassName}>{formattedFee}</p>
                <p className={pricingClassName}>{taxDescription}</p>
            </div>
        );
    }

    return (
        <div
            className={PRICE_PER_TICKET_PRICING_CELLS_CLASS_NAME}
            role="gridcell"
        >
            {planFeeInfo}
            {perTicketLabel}
        </div>
    );
};

/*
Helper function to extract fee components to build multiple table rows with the data.

This probably doesn't need to be split into a helper function,
but having this separate makes an eventual refactor from the double map
to reduce easier.
*/
const _getPricingLinesData = (
    packageTypes = DEFAULT_PACKAGE_TYPES,
    feeBreakdown,
    assortmentsTax,
    isSimplePricing = false,
    showMaxFee = false,
    planIndex = null,
) => {
    const pricePerPaidTicketPerPlanData = packageTypes.map((packageName) => (
        <ItemPriceCell
            key={packageName}
            isSimplePricing={isSimplePricing}
            showMaxFee={showMaxFee}
            taxInformation={assortmentsTax[packageName]}
            {...feeBreakdown[packageName]}
            planIndex={planIndex}
        />
    ));
    const paymentProcessingFeePerPlanData = packageTypes.map(
        (packageName) => feeBreakdown?.[packageName]?.formattedProcessingFee,
    );

    const priceUnderCappedFeeThreshold = packageTypes.map((packageName) => {
        const feeProps = {
            ...feeBreakdown[packageName],
        };

        if (feeBreakdown[packageName].formattedCappedFee) {
            feeProps.formattedFee =
                feeBreakdown[packageName].formattedCappedFee;
        }
        return (
            <ItemPriceCell
                key={`${packageName}-capped`}
                isSimplePricing={isSimplePricing}
                {...feeProps}
                taxInformation={assortmentsTax[packageName]}
                planIndex={planIndex}
            />
        );
    });

    return {
        pricePerPaidTicketPerPlanData,
        paymentProcessingFeePerPlanData,
        priceUnderCappedFeeThreshold,
    };
};

/*
Helper function to get annual fee data depending on locale.

Used for Beyond Free smokescreen experiment. When experiment has been concluded,
get data from API instead of hard-coding fees.
*/
const _getAnnualFeeData = (locale, packagesTotal) => {
    let annualFeeData = [
        ANNUAL_FEE_NONE,
        ANNUAL_FEE_NONE,
        ANNUAL_PRICE_PER_LOCALE[locale],
    ];

    if (packagesTotal >= 4) {
        annualFeeData = [...annualFeeData, ANNUAL_FEE_CUSTOM];
    }

    return annualFeeData;
};

/*
Helper function to get threshold value from feebreakdown.

EXPERIMENT_MARKER=LOW_ATV_CAPPED_FEE
*/
const _getCappedFeeThreshold = (feeBreakdown) =>
    Object.values(feeBreakdown)[0].formattedFeeThreshold;

const AssembleItemPriceCell = ({
    packageObject,
    pricingItem: { isSimplePricing, showMaxFee, assortmentsTax, planIndex },
}) =>
    Object.entries(packageObject).map(([packageName, packageValue]) => (
        <ItemPriceCell
            key={packageName}
            isSimplePricing={isSimplePricing}
            showMaxFee={showMaxFee}
            taxInformation={assortmentsTax[packageName]}
            {...packageValue}
            planIndex={planIndex}
        />
    ));

const PricingPerItemType = ({ feeBreakdown, packagesTotal, ...pricingItem }) =>
    Object.entries(feeBreakdown).reduce(
        (acc, [itemType, packageObject]) =>
            ITEM_TYPES_SHOWN_IN_PRICING_PAGE.includes(itemType)
                ? [
                      ...acc,
                      <FeatureLine
                          key={itemType}
                          title={ROW_TITLE_PER_ITEM_TYPE[itemType]}
                          perPlanData={AssembleItemPriceCell({
                              packageObject,
                              pricingItem,
                          })}
                          packagesTotal={packagesTotal}
                      />,
                  ]
                : acc,
        [],
    );

/*
Pricing rows (e.g "Price per paid ticket", "Payment processing")
*/
const PricingBlock = ({
    locale,
    packageTypes,
    packagesTotal,
    feeBreakdown,
    assortmentsTax,
    paymentProcessingFeature,
    planIndex = null,
    extraMainFeatures = [],
    isSimplePricing,
    showMaxFee,
    pricePerFreeTicket,
    pricePerPaidTicket,
    shouldShowAnnualFee,
    shouldShowCappedFee,
    useRefactoredPricingTableComparison,
}) => {
    const {
        pricePerPaidTicketPerPlanData,
        paymentProcessingFeePerPlanData,
        priceUnderCappedFeeThreshold,
    } = _getPricingLinesData(
        packageTypes,
        feeBreakdown.ticket || feeBreakdown,
        assortmentsTax,
        isSimplePricing,
        showMaxFee,
        planIndex,
        useRefactoredPricingTableComparison,
        packagesTotal,
    );
    let pricingPerItemType = null;

    if (
        useRefactoredPricingTableComparison &&
        LOCALES_WITH_MULTIPLE_ITEM_TYPES_PRICES.includes(locale)
    ) {
        pricingPerItemType = (
            <PricingPerItemType
                feeBreakdown={feeBreakdown}
                isSimplePricing={isSimplePricing}
                showMaxFee={showMaxFee}
                assortmentsTax={assortmentsTax}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
        );
    }

    const paymentProcessingFeeLineData = {
        ...paymentProcessingFeature,
        perPlanData: paymentProcessingFeePerPlanData,
    };

    const mainFeaturesShown = extraMainFeatures.map(
        ({ title, perPlanData }, index) => (
            <FeatureLine
                key={`extraMainFeature-${index}`}
                title={title}
                perPlanData={perPlanData}
                packageTypes={packageTypes}
                packagesTotal={packagesTotal}
                planIndex={planIndex}
            />
        ),
    );

    let paymentProcessingLine = null;
    let annualFeeLine = null;
    let cappedFeeLine = null;

    if (!isSimplePricing) {
        paymentProcessingLine = (
            <FeatureLine
                {...paymentProcessingFeeLineData}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
        );
    }

    if (shouldShowAnnualFee) {
        annualFeeLine = (
            <FeatureLine
                title={ANNUAL_FEE_TITLE}
                perPlanData={_getAnnualFeeData(locale, packagesTotal)}
                packagesTotal={packagesTotal}
                planIndex={planIndex}
            />
        );
    }

    // EXPERIMENT_MARKER=LOW_ATV_CAPPED_FEE
    let paidTicketTitle = pricePerPaidTicket;

    if (shouldShowCappedFee) {
        const threshold = _getCappedFeeThreshold(feeBreakdown);

        paidTicketTitle = getCappedFeeTitleOverThreshold(threshold);
        cappedFeeLine = (
            <FeatureLine
                title={getCappedFeeTitleUnderThreshold(threshold)}
                perPlanData={priceUnderCappedFeeThreshold}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
        );
    }

    let pricingSection = (
        <PricingSection
            pricingPerItemType={pricingPerItemType}
            paidTicketTitle={paidTicketTitle}
            pricePerPaidTicketPerPlanData={pricePerPaidTicketPerPlanData}
            planIndex={planIndex}
            packagesTotal={packagesTotal}
            pricePerFreeTicket={pricePerFreeTicket}
            paymentProcessingLine={paymentProcessingLine}
        />
    );

    if (isTldAllowedForNewPricingPage) {
        pricingSection = (
            <PricingAndPackagingSection
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
        );
    }

    return (
        <div className="eds-l-pad-bot-4" role="grid">
            {isTldAllowedForNewPricingPage ? (
                <div
                    className="eds-l-pad-bot-6 eds-l-pad-top-10 eds-align--center-vertical"
                    style={{ width: 1180 }}
                >
                    <div className={FEATURE_BLOCK_ICON_CLASS_NAME}></div>
                    <section className="eds-g-cell eds-g-cell-4-12">
                        <h1 className="eds-text-bl eds-text--left eds-text-weight--heavy">
                            {PLAN_DETAILS}
                        </h1>
                    </section>
                </div>
            ) : null}
            {annualFeeLine}
            {cappedFeeLine}
            {pricingSection}
            {mainFeaturesShown}
        </div>
    );
};

const PricingAndPackagingSection = ({ planIndex, packagesTotal }) => {
    const { serviceFee, processingFee } = getFeesForTld();
    let paymentProcessingFee = gettext('None');
    if (processingFee) {
        paymentProcessingFee = gettext('%(fee)s per paid order', {
            fee: processingFee.percent,
        }).toString();
    }
    const dynamicFeesText = gettext(
        '%(serviceFeePercent)s + %(additionalPerTicketCost)s per paid ticket',
        {
            serviceFeePercent: serviceFee.percent,
            additionalPerTicketCost: serviceFee.additionalPerTicketCost,
        },
    ).toString();

    return (
        <>
            <FeatureLine
                title={gettext('Organizer Fee (fee to publish)')}
                link={
                    '/help/en-us/articles/193833/eventbrites-new-pricing-plans/'
                }
                perPlanData={getOrganizerFeePerPlan()}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
            <FeatureLine
                title={gettext('Ticketing Fees (paid by attendees)')}
                link={
                    '/help/en-us/articles/755615/how-much-does-it-cost-for-organizers-to-use-eventbrite/'
                }
                perPlanData={[]}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
            <FeatureLine
                title={gettext('Service Fee')}
                perPlanData={[
                    ...Array(3).fill(dynamicFeesText),
                    gettext('Custom'),
                ]}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
                isChild={true}
            />
            <FeatureLine
                title={gettext('Payment Processing Fee')}
                perPlanData={Array(4).fill(paymentProcessingFee)}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
                isChild={true}
            />

            <FeatureLine
                title={gettext(
                    'Ability to absorb Ticketing Fees for your attendees',
                )}
                perPlanData={[true, true, true, true]}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
                isChild={true}
            />
        </>
    );
};

const FeaturePricingSection = ({
    packageTypes,
    feeBreakdown,
    planIndex,
    packagesTotal,
    paymentProcessingLine,
}) => {
    const serviceFeePerTicket = packageTypes.map(
        (packageName) => feeBreakdown[packageName]?.formattedFee,
    );

    return (
        <>
            <FeatureLine
                title={gettext('Eventbrite service fee per ticket')}
                perPlanData={serviceFeePerTicket}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
            <FeatureLine
                title={gettext('No upfront fees')}
                perPlanData={[true, true]}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
            <FeatureLine
                title={gettext('Fee per free ticket')}
                perPlanData={[gettext('Free'), gettext('Free')]}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
            {paymentProcessingLine}
            <FeatureLine
                title={gettext('Payouts ahead of time')}
                perPlanData={[true, gettext('Custom')]}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
            <FeatureLine
                title={gettext(
                    'Ability to pass online ticket fees to attendees',
                )}
                perPlanData={[true, true]}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
        </>
    );
};

const PricingSection = ({
    pricingPerItemType,
    paidTicketTitle,
    pricePerPaidTicketPerPlanData,
    planIndex,
    packagesTotal,
    pricePerFreeTicket,
    paymentProcessingLine,
}) => {
    return (
        <>
            {pricingPerItemType || (
                <FeatureLine
                    title={paidTicketTitle}
                    perPlanData={pricePerPaidTicketPerPlanData}
                    planIndex={planIndex}
                    packagesTotal={packagesTotal}
                />
            )}
            <FeatureLine
                {...pricePerFreeTicket}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
            />
            {paymentProcessingLine}
        </>
    );
};

/*
Icon to represent feature availability (e.g. a check mark)
*/
const FeatureAvailabilityIcon = ({ isAvailable, packagesTotal }) => {
    const iconType = isAvailable ? AVAILABLE_ICON_TYPE : UNAVAILABLE_ICON_TYPE;
    const iconColor = isAvailable
        ? AVAILABLE_ICON_COLOR
        : UNAVAILABLE_ICON_COLOR;

    const featureAvailabilityClass = classNames({
        [FEATURE_AVAILABILITY_ICON_CLASS_NAME_DEFAULT]: packagesTotal >= 3,
        [FEATURE_AVAILABILITY_ICON_CLASS_NAME_TWO_PACKAGES]: packagesTotal <= 2,
    });

    return (
        <div className={featureAvailabilityClass} role="gridcell">
            <Icon type={iconType} size="small" color={iconColor} />
        </div>
    );
};

/*
String to represent feature availability (e.g. "Unlimited")
*/
const FeatureAvailabilityString = ({ availablityString, packagesTotal }) => {
    const featureAvailabilityClass = classNames({
        [FEATURE_AVAILABILITY_STRING_CLASS_NAME_DEFAULT]: packagesTotal >= 3,
        [FEATURE_AVAILABILITY_STRING_CLASS_NAME_TWO_PACKAGES]:
            packagesTotal <= 2,
    });
    return (
        <div className={featureAvailabilityClass} role="gridcell">
            <p className="eds-l-pad-hor-2">{availablityString}</p>
        </div>
    );
};

/*
This function enables planData elements to be "any" (HTML/node, string, bool) type.

This isn't strictly necessary, but provides additional functionality if we want to
render other kinds of things for each plan down the road (like a string with custom formatting).
*/
const _getFeatureAvailabilityCell = ({
    isAvailable,
    index = 0,
    packagesTotal,
}) => {
    let featureAvailabilityCell = null;

    if (React.isValidElement(isAvailable)) {
        featureAvailabilityCell = isAvailable;
    } else if (
        _.isString(isAvailable) ||
        isAvailable instanceof GenericLazyString
    ) {
        featureAvailabilityCell = (
            <FeatureAvailabilityString
                key={`isAvailable-${index}`}
                availablityString={isAvailable}
                packagesTotal={packagesTotal}
            />
        );
    } else {
        featureAvailabilityCell = (
            <FeatureAvailabilityIcon
                key={`isAvailable-${index}`}
                isAvailable={isAvailable}
                packagesTotal={packagesTotal}
            />
        );
    }

    return featureAvailabilityCell;
};

/*
A complete feature line, including a label and availability data for each plan
*/
const FeatureLine = ({
    title,
    link,
    perPlanData,
    planIndex = null,
    packagesTotal,
    isChild = false,
}) => {
    let availabilityInfo = null;
    let featureTitle = title;
    const featureLineTitleClasses = classNames(
        'eds-text--left eds-g-offset-0-12 eds-g-offset-mw-1-12 eds-g-cell eds-g-cell-6-12',
        {
            'eds-l-pad-vert-3': isTldAllowedForNewPricingPage,
            'eds-g-cell-mw-4-12 eds-g-cell-ln-4-12 eds-g-cell-lg-4-12 eds-g-cell-lw-4-12':
                packagesTotal <= 3,
            'eds-g-cell-mn-4-12 eds-g-cell-mw-3-12 eds-g-cell-ln-3-12 eds-g-cell-lg-3-12 eds-g-cell-lw-3-12':
                packagesTotal > 3 && !isTldAllowedForNewPricingPage,
            'eds-g-cell-mn-6-12 eds-g-cell-mw-3-12 eds-g-cell-ln-3-12 eds-g-cell-lg-3-12 eds-g-cell-lw-3-12':
                packagesTotal > 3 && isTldAllowedForNewPricingPage,
        },
    );

    const dividerClasses = classNames({
        [FEATURE_LINE_DIVIDER_LONG_CLASS_NAME]: packagesTotal >= 4,
        [FEATURE_LINE_DIVIDER_MEDIUM_CLASS_NAME]: packagesTotal <= 3,
    });

    if (planIndex !== null) {
        const isAvailable = perPlanData[planIndex];

        availabilityInfo = _getFeatureAvailabilityCell({
            isAvailable,
            packagesTotal,
        });
    } else {
        availabilityInfo = perPlanData.map((isAvailable, index) =>
            _getFeatureAvailabilityCell({ isAvailable, index, packagesTotal }),
        );
    }

    if (link) {
        featureTitle = (
            <a href={link} rel="noopener noreferrer" target="_blank">
                {title}
            </a>
        );
    }

    return (
        <div className="eds-g-grid" role="row">
            <div
                className={
                    isTldAllowedForNewPricingPage
                        ? FEATURE_LINE_CONTAINER_NOPAD_CLASS_NAME
                        : FEATURE_LINE_CONTAINER_CLASS_NAME
                }
            >
                <div className={featureLineTitleClasses} role="gridcell">
                    <p style={isChild ? { paddingLeft: '24px' } : {}}>
                        {featureTitle}
                    </p>
                </div>
                {availabilityInfo}
            </div>
            {!isTldAllowedForNewPricingPage ? (
                <div className={dividerClasses}>
                    <Divider />
                </div>
            ) : null}
        </div>
    );
};

/*
A collection of feature lines (e.g. "Start Selling")
*/
const FeaturesBlock = ({
    heading,
    iconType,
    features,
    planIndex,
    packagesTotal,
}) => {
    const featureLines = features.map(
        ({ title, link, perPlanData, isChild = false }) => (
            <FeatureLine
                key={`${title.toString()}`}
                title={title}
                link={link}
                perPlanData={perPlanData}
                planIndex={planIndex}
                packagesTotal={packagesTotal}
                isChild={isChild}
            />
        ),
    );

    return (
        <div role="grid">
            <div
                className="eds-l-pad-bot-6 eds-l-pad-top-10 eds-align--center-vertical"
                style={{ width: 1180 }}
            >
                <div className={FEATURE_BLOCK_ICON_CLASS_NAME}>
                    {!!iconType && (
                        <Icon
                            type={iconType}
                            size="small"
                            color={AVAILABLE_ICON_COLOR}
                        />
                    )}
                </div>

                <section className="eds-g-cell eds-g-cell-4-12">
                    <h1 className="eds-text-bl eds-text--left eds-text-weight--heavy">
                        {heading}
                    </h1>
                </section>
            </div>
            {featureLines}
        </div>
    );
};

/*
A helper function that iterates over the collections of features defined in
the settings and generates Feature Blocks from the data
*/
const _getFeaturesBlocks = (sections, planIndex, packagesTotal) =>
    _.toArray(sections).map(({ heading, iconType, features }, i) => (
        <FeaturesBlock
            key={i}
            heading={heading}
            iconType={iconType}
            features={features}
            planIndex={planIndex}
            packagesTotal={packagesTotal}
        />
    ));

export default class FeatureComparisonTable extends PureComponent {
    render() {
        const {
            locale,
            packageTypes,
            feeBreakdown,
            assortmentsTax,
            paymentProcessingFeature,
            sections,
            planIndex,
            isSimplePricing,
            showMaxFee,
            extraMainFeatures,
            pricePerPaidTicket,
            pricePerFreeTicket,
            shouldShowAnnualFee,
            shouldShowCappedFee,
            packagesTotal,
            useRefactoredPricingTableComparison,
        } = this.props;
        // locale is being passed for the BF smokescreen.
        // when experiment has concluded, please remove locale and use
        // international settings instead
        const features = _getFeaturesBlocks(sections, planIndex, packagesTotal);

        const featureComparisonTableClassName = classNames(
            {
                'eds-text-color--ui-900': isTldAllowedForNewPricingPage,
                'eds-text-color--grey-600': !isTldAllowedForNewPricingPage,
            },
            'eds-l-pad-hor-4',
            'eds-l-mn-pad-hor-6',
            'eds-l-md-pad-hor-6',
            {
                'eds-l-pad-bot-20': !isSimplePricing,
                'marketing-container__max-width--medium-wide':
                    packagesTotal <= 2,
                'marketing-container__max-width': packagesTotal >= 3,
            },
        );

        return (
            <div className={featureComparisonTableClassName} role="grid">
                <PricingBlock
                    locale={locale}
                    packageTypes={packageTypes}
                    packagesTotal={packagesTotal}
                    feeBreakdown={feeBreakdown}
                    assortmentsTax={assortmentsTax}
                    paymentProcessingFeature={paymentProcessingFeature}
                    planIndex={planIndex}
                    isSimplePricing={isSimplePricing}
                    showMaxFee={showMaxFee}
                    extraMainFeatures={extraMainFeatures}
                    pricePerPaidTicket={pricePerPaidTicket}
                    pricePerFreeTicket={pricePerFreeTicket}
                    shouldShowAnnualFee={shouldShowAnnualFee}
                    shouldShowCappedFee={shouldShowCappedFee}
                    useRefactoredPricingTableComparison={
                        useRefactoredPricingTableComparison
                    }
                />
                {features}
            </div>
        );
    }
}
